import type { BuildingBounds } from "building-types/building/buildingBounds";
import type { Building } from "building-types/building/buildingTypes";
import { SheetCustomScales } from "building-types/settings/pageElevationTypes";
import { PlanLineStyle } from "building-types/settings/settingsTypesEnum";
import { ZERO_INCHES } from "common/constants/scaleFactors";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import { numMul, numSub } from "common/utils/nominalNumbers";
import { inches } from "doc/pdf/drawingLayout";
import { AVAILABLE_SCALE_FACTORS } from "lib/house/annotation/DwgScale";
import { getRenderSizeWithTitleBlock } from "lib/house/pdf/getRenderSizeWithTitleBlock";

const SCALE_CALCULATION_PADDING = 72; // In inches in world space. TODO: Paper space padding could make more sense.
export const DRAWING_LABEL_PADDING = inches(1);
const MINIMUM_DRAWING_SCALE = 48;

/**
 * Calculates the drawing scale to use for all elevation drawings. Constructs a bounding box using building height
 * and either width or depth plus a small amount of padding, then finds the smallest scale that accommmodates all drawings.
 * Assumes that below-grade elements will not be drawn.
 */
function calculateElevationDrawingScale(
  buildingBounds: BuildingBounds,
  renderWidth: SheetInches,
  renderHeight: SheetInches,
  addBelowGroundLines: boolean,
) {
  const keys = Object.keys(AVAILABLE_SCALE_FACTORS);

  const height = addBelowGroundLines
    ? buildingBounds.highestRoofPoint - buildingBounds.buildingBottomElevation
    : buildingBounds.highestRoofPoint;

  const frontBackExactScale = Math.max(
    (buildingBounds.width + SCALE_CALCULATION_PADDING) / renderWidth,
    (height + SCALE_CALCULATION_PADDING) / renderHeight,
  );

  const leftRightExactScale = Math.max(
    (buildingBounds.depth + SCALE_CALCULATION_PADDING) / renderWidth,
    (height + SCALE_CALCULATION_PADDING) / renderHeight,
  );

  const exactScale = Math.max(frontBackExactScale, leftRightExactScale);
  const scale = Number(keys.find((val) => Number(val) > exactScale)) ?? Number(keys[keys.length - 1]);
  return Math.max(scale, MINIMUM_DRAWING_SCALE);
}

export function getElevationScaleOverride(
  building: Building,
  width: SheetInches,
  height: SheetInches,
  addElevationName?: boolean,
): number {
  const planSettings = building.settings.planset;
  const elevationScaleOverride = planSettings.elevationScaleOverride;

  const drawingLabelPadding = addElevationName ? DRAWING_LABEL_PADDING : ZERO_INCHES;

  if (!elevationScaleOverride || elevationScaleOverride === SheetCustomScales.SCALE_AUTO) {
    return calculateElevationDrawingScale(
      building.buildingBounds,
      width,
      numSub(height, drawingLabelPadding),
      planSettings.belowGradeRenderStyle !== PlanLineStyle.NONE || planSettings.overlayFoundationOutlines,
    );
  }

  switch (elevationScaleOverride) {
    case SheetCustomScales.SCALE_1_4:
      return 48;
    case SheetCustomScales.SCALE_1_8:
      return 24;
    case SheetCustomScales.SCALE_1_16:
      return 12;
    case SheetCustomScales.SCALE_1_2:
      return 96;
  }
}

export function getElevationScaleOverrideWithDefaultPageDimensions(building: Building, addElevationName?: boolean) {
  const renderDims = getRenderSizeWithTitleBlock();
  const width = renderDims.renderWidth;
  const height = numMul(renderDims.renderHeight, 0.5);
  return getElevationScaleOverride(building, width, height, addElevationName);
}
